import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';

const LegalPage = ({ docType }) => {
  const [doc, setDoc] = useState(null);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [docRes, siteRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal/${docType}`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/config`),
        ]);
        if (!docRes.ok) throw new Error('Document not found');
        setDoc(await docRes.json());
        if (siteRes.ok) setSite(await siteRes.json());
      } catch (e) {
        setError(e.message || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [docType]);

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink font-body">
      <header className="border-b border-brand-border/60">
        <nav className="container mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="legal-home-link">
            <img
              src="/logo.png"
              alt={site?.company_name || 'ReflectiveMind'}
              className="h-12 sm:h-14 w-auto"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center text-sm text-brand-ink-muted hover:text-brand-ink transition-colors"
            data-testid="legal-back-link"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 lg:px-12 py-16 lg:py-24 max-w-3xl">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`legal-page-${docType}`}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-pulse w-2 h-2 bg-brand-cta rounded-full" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-brand-ink font-display text-3xl">A momentary pause.</p>
              <p className="mt-3 text-brand-ink-muted">{error}</p>
              <Link to="/" className="text-brand-accent hover:text-brand-cta mt-6 inline-block underline-offset-4 hover:underline">
                Return home
              </Link>
            </div>
          ) : (
            <div className="legal-content prose prose-stone max-w-none">
              <ReactMarkdown>{doc.content}</ReactMarkdown>
            </div>
          )}
        </motion.article>
      </main>

      <footer className="border-t border-brand-border/60">
        <div className="container mx-auto px-6 lg:px-12 py-10 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-brand-sage">
            &copy; {site?.copyright_year || new Date().getFullYear()}{' '}
            {site?.company_name || 'ReflectiveMind'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export const PrivacyPage = () => <LegalPage docType="privacy" />;
export const TermsPage = () => <LegalPage docType="terms" />;

export default LegalPage;
